import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { authHeaders } from '../utils/authService';

const ESRI_TILE_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

const SOURCE_COLORS = {
  'web-login': '#3498db',
  'sim-login': '#2ecc71',
  'desktop-login': '#f39c12',
  'sim-upload': '#e74c3c',
  'app-upload': '#9b59b6',
  'sim-checkin': '#00e5ff',
};

const SOURCE_LABELS = {
  'web-login': 'Web Login',
  'sim-login': 'Sim Login',
  'desktop-login': 'Desktop Login',
  'sim-upload': 'Sim Upload',
  'app-upload': 'App Upload',
  'sim-checkin': 'Sim Checkin',
};

function FitBounds({ markers, fitKey }) {
  const map = useMap();
  useEffect(() => {
    if (markers.length === 0) return;
    const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
  }, [markers, map, fitKey]);
  return null;
}

function MapRef({ mapRef }) {
  const map = useMap();
  useEffect(() => { mapRef.current = map; }, [map, mapRef]);
  return null;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function IpAddressMap({ users }) {
  const [simCheckins, setSimCheckins] = useState([]);
  const [showList, setShowList] = useState(false);
  const [fitKey, setFitKey] = useState(0);
  const mapRef = useRef(null);

  useEffect(() => {
    fetch('/api/admin/sim-checkins', { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setSimCheckins(data); })
      .catch(() => {});
  }, []);

  // Build marker data: one entry per unique IP, with list of users at that IP
  const markers = useMemo(() => {
    const ipMap = new Map();

    // User-associated IPs
    for (const user of users) {
      if (!user.ipAddresses?.length) continue;
      for (const entry of user.ipAddresses) {
        if (entry.lat == null || entry.lng == null) continue;

        if (!ipMap.has(entry.ip)) {
          ipMap.set(entry.ip, {
            ip: entry.ip,
            lat: entry.lat,
            lng: entry.lng,
            city: entry.city,
            region: entry.region,
            country: entry.country,
            source: entry.source,
            users: [],
          });
        }
        ipMap.get(entry.ip).users.push({
          name: user.displayName || user.email,
          email: user.email,
          source: entry.source,
          firstSeen: entry.firstSeen,
          lastSeen: entry.lastSeen,
        });
      }
    }

    // Anonymous sim check-in IPs
    for (const entry of simCheckins) {
      if (entry.lat == null || entry.lng == null) continue;
      if (ipMap.has(entry.ip)) {
        ipMap.get(entry.ip).users.push({
          name: 'RaceVoiceSim (anonymous)',
          email: '—',
          source: 'sim-checkin',
          firstSeen: entry.firstSeen,
          lastSeen: entry.lastSeen,
          hits: entry.hits,
        });
      } else {
        ipMap.set(entry.ip, {
          ip: entry.ip,
          lat: entry.lat,
          lng: entry.lng,
          city: entry.city,
          region: entry.region,
          country: entry.country,
          source: 'sim-checkin',
          users: [{
            name: 'RaceVoiceSim (anonymous)',
            email: '—',
            source: 'sim-checkin',
            firstSeen: entry.firstSeen,
            lastSeen: entry.lastSeen,
            hits: entry.hits,
          }],
        });
      }
    }

    return Array.from(ipMap.values());
  }, [users, simCheckins]);

  const usersWithIps = useMemo(() => {
    return users.filter((u) => u.ipAddresses?.some((e) => e.lat != null));
  }, [users]);

  // Sort list by most recent lastSeen
  const sortedMarkers = useMemo(() => {
    return [...markers].sort((a, b) => {
      const aMax = Math.max(...a.users.map((u) => new Date(u.lastSeen || 0).getTime()));
      const bMax = Math.max(...b.users.map((u) => new Date(u.lastSeen || 0).getTime()));
      return bMax - aMax;
    });
  }, [markers]);

  const handleShowAll = useCallback(() => {
    setFitKey((k) => k + 1);
  }, []);

  const handleFocusIp = useCallback((m) => {
    const map = mapRef.current;
    if (!map) return;
    map.setView([m.lat, m.lng], 10, { animate: true });
  }, []);

  const btnStyle = {
    padding: '5px 14px', borderRadius: 4, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 600,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Stats bar */}
      <div style={{
        display: 'flex', gap: 24, padding: '10px 16px',
        background: '#1a1a2e', borderRadius: 8, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <span style={{ color: '#ccc', fontSize: 14 }}>
          <strong style={{ color: '#fff' }}>{markers.length}</strong> unique IPs from{' '}
          <strong style={{ color: '#fff' }}>{usersWithIps.length}</strong> users
          {simCheckins.length > 0 && (
            <> + <strong style={{ color: '#fff' }}>{simCheckins.length}</strong> sim checkins</>
          )}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleShowAll}
            style={{ ...btnStyle, background: '#2e86c1', color: '#fff' }}
          >
            Show All
          </button>
          <button
            onClick={() => setShowList((v) => !v)}
            style={{ ...btnStyle, background: showList ? '#555' : '#374151', color: '#fff' }}
          >
            {showList ? 'Hide List' : 'IP List'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 16, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {Object.entries(SOURCE_LABELS).map(([key, label]) => (
            <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#aaa' }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: SOURCE_COLORS[key], display: 'inline-block',
              }} />
              {label}
            </span>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        {/* IP List panel */}
        {showList && (
          <div style={{
            width: 340, flexShrink: 0, background: '#1a1a2e', borderRadius: 8,
            overflow: 'auto', maxHeight: 'calc(100vh - 280px)', minHeight: 400,
          }}>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #333', position: 'sticky', top: 0, background: '#1a1a2e', zIndex: 1 }}>
              <span style={{ color: '#aaa', fontSize: 13, fontWeight: 600 }}>{sortedMarkers.length} unique IPs</span>
            </div>
            {sortedMarkers.map((m) => (
              <div
                key={m.ip}
                onClick={() => handleFocusIp(m)}
                style={{
                  padding: '8px 12px', borderBottom: '1px solid #222', cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#252545'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: SOURCE_COLORS[m.source] || '#3498db',
                  }} />
                  <span style={{ color: '#fff', fontSize: 13, fontFamily: 'monospace' }}>{m.ip}</span>
                </div>
                <div style={{ color: '#888', fontSize: 12, marginTop: 2, marginLeft: 16 }}>
                  {[m.city, m.region, m.country].filter(Boolean).join(', ') || 'Unknown location'}
                </div>
                {m.users.map((u, i) => (
                  <div key={i} style={{ marginLeft: 16, marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      display: 'inline-block', padding: '0 5px', borderRadius: 3, fontSize: 10,
                      background: SOURCE_COLORS[u.source] || '#3498db', color: '#fff',
                    }}>
                      {SOURCE_LABELS[u.source] || u.source}
                    </span>
                    <span style={{ color: '#aaa', fontSize: 12 }}>{u.name}</span>
                    {u.hits > 1 && <span style={{ color: '#666', fontSize: 11 }}>({u.hits}x)</span>}
                  </div>
                ))}
                <div style={{ color: '#555', fontSize: 11, marginTop: 2, marginLeft: 16 }}>
                  Last seen: {formatDate(Math.max(...m.users.map((u) => new Date(u.lastSeen || 0).getTime())) ? m.users.reduce((a, b) => new Date(a.lastSeen || 0) > new Date(b.lastSeen || 0) ? a : b).lastSeen : null)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Map */}
        <div style={{ borderRadius: 8, overflow: 'hidden', height: 'calc(100vh - 280px)', minHeight: 400, flex: 1 }}>
          <MapContainer
            center={[39, -98]}
            zoom={4}
            style={{ width: '100%', height: '100%' }}
            scrollWheelZoom={true}
          >
            <MapRef mapRef={mapRef} />
            <TileLayer
              url={ESRI_TILE_URL}
              attribution="Tiles &copy; Esri"
              maxZoom={18}
            />
            {markers.length > 0 && <FitBounds markers={markers} fitKey={fitKey} />}
            {markers.map((m) => (
              <CircleMarker
                key={m.ip}
                center={[m.lat, m.lng]}
                radius={8}
                pathOptions={{
                  fillColor: SOURCE_COLORS[m.source] || '#3498db',
                  color: '#fff',
                  weight: 2,
                  fillOpacity: 0.85,
                }}
              >
                <Popup>
                  <div style={{ minWidth: 200, fontFamily: 'sans-serif', fontSize: 13 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{m.ip}</div>
                    <div style={{ color: '#666', marginBottom: 8 }}>
                      {[m.city, m.region, m.country].filter(Boolean).join(', ')}
                    </div>
                    {m.users.map((u, i) => (
                      <div key={i} style={{
                        padding: '6px 0',
                        borderTop: i > 0 ? '1px solid #eee' : 'none',
                      }}>
                        <div style={{ fontWeight: 600 }}>{u.name}</div>
                        {u.email !== '—' && <div style={{ color: '#888', fontSize: 12 }}>{u.email}</div>}
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                          <span style={{
                            display: 'inline-block', padding: '1px 6px', borderRadius: 3,
                            background: SOURCE_COLORS[u.source] || '#3498db', color: '#fff', marginRight: 6,
                          }}>
                            {SOURCE_LABELS[u.source] || u.source}
                          </span>
                          {formatDate(u.lastSeen)}
                          {u.hits > 1 && <span style={{ marginLeft: 6 }}>({u.hits} hits)</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
